import { useTranslation } from "@repo/core";
import { formatPercentage } from "../../../utils/numberFormat";
import { HiArrowDown, HiArrowUp, HiExternalLink, HiEye } from "react-icons/hi";
import type { Stock } from "./PortfolioCarousel";
import StockAvatar from "./StockAvatar";
import type { OutlierTicker } from "../../../hooks/api/useUniversesApi";
import { Tooltip } from "flowbite-react";

const FUNDAMENTALS_MONITORING_URL = "https://mta.pef.mendelu.cz/divergence/";

const getFundamentalsMonitoringUrl = (ticker: string, factor: string) =>
  `${FUNDAMENTALS_MONITORING_URL}?${new URLSearchParams({ ticker, fundamentals: factor })}`;

const StockList = ({
  title,
  performance,
  stocks,
  stockDetails,
  outliersByTicker,
  factor,
}: {
  title: string;
  performance: number;
  stocks: Stock[];
  stockDetails: { [key: string]: { name: string; logo: string } };
  outliersByTicker?: Map<string, OutlierTicker>;
  factor?: string;
}) => {
  const { tPortfolio } = useTranslation();

  return (
    <div className="border-gray-200 p-4">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>

      <div className="mb-4">
        <p className="text-sm text-gray-600 mb-1">
          {tPortfolio("totalPerformance")}
        </p>
        <div
          className={`inline-flex items-center gap-1 px-2 py-1 text-sm font-medium`}
        >
          {performance >= 0 ? (
            <HiArrowUp size={14} />
          ) : (
            <HiArrowDown size={14} />
          )}
          {performance >= 0 ? "+" : ""}
          {formatPercentage(performance)}
        </div>
      </div>

      <div className="space-y-3">
        {stocks.map((stock) => {
          // Check if this ticker has outliers
          const outlierData = outliersByTicker?.get(stock.ticker);
          const hasOutliers = !!outlierData;

          // Count total outlier observations for tooltip
          const totalOutliers = outlierData?.problems.reduce(
            (sum, problem) => sum + problem.observations.length,
            0
          ) ?? 0;

          return (
            <div key={stock.id} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <StockAvatar
                  stockDetails={stockDetails}
                  ticker={stock.ticker}
                  companyName={stock.companyName}
                />
                <div className="flex items-center gap-2">
                  <div>
                    <p className="font-medium text-gray-900 text-sm">
                      {stock.ticker}
                    </p>
                    <p className="text-xs text-gray-600">
                      {stockDetails[stock.ticker]?.name}
                    </p>
                  </div>
                  {/* Outlier indicator */}
                  {hasOutliers && (
                    <Tooltip
                      content={
                        <div className="max-w-md">
                          <p className="font-semibold mb-2 text-sm">{tPortfolio("significantDataChange")}</p>
                          <p className="text-xs mb-3">
                            {totalOutliers === 1
                              ? tPortfolio("outlierObservationsDetected", { count: totalOutliers })
                              : tPortfolio("outlierObservationsDetected_plural", { count: totalOutliers })}
                          </p>

                          {factor && (
                            <a
                              href={getFundamentalsMonitoringUrl(outlierData.ticker, factor)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-amber-100 underline hover:text-white mb-3"
                            >
                              {tPortfolio("openFundamentalsMonitoring")}
                              <HiExternalLink className="w-3 h-3" />
                            </a>
                          )}

                          {outlierData.problems.map((problem, problemIdx) => (
                            <div key={problemIdx} className="mb-3 last:mb-0">
                              <p className="text-xs font-semibold text-amber-200 mb-1">
                                {problem.field} ({problem.observations.length === 1
                                  ? tPortfolio("anomalyCount", { count: problem.observations.length })
                                  : tPortfolio("anomalyCount_plural", { count: problem.observations.length })})
                              </p>

                              {/* Show up to 5 most recent observations */}
                              <div className="space-y-1 pl-2">
                                {problem.observations
                                  .slice(0, 5)
                                  .map((obs, obsIdx) => (
                                    <div key={obsIdx} className="text-xs font-mono">
                                      <span className="text-amber-100">{obs.date}</span>
                                      <span className="text-gray-300 mx-1">→</span>
                                      <span className="text-white font-semibold">
                                        {obs.divergent_value.toFixed(4)}
                                      </span>
                                      <span className="text-gray-400 text-[10px] ml-1">
                                        ({tPortfolio("expectedRange", {
                                          lower: obs.interval_lower_boundary.toFixed(4),
                                          upper: obs.interval_upper_boundary.toFixed(4),
                                        })})
                                      </span>
                                    </div>
                                  ))}

                                {/* Show "and X more" if there are more observations */}
                                {problem.observations.length > 5 && (
                                  <p className="text-[10px] text-gray-400 italic mt-1">
                                    {problem.observations.length - 5 === 1
                                      ? tPortfolio("moreObservations", { count: problem.observations.length - 5 })
                                      : tPortfolio("moreObservations_plural", { count: problem.observations.length - 5 })}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      }
                      placement="top"
                    >
                      <div className="flex items-center justify-center w-5 h-5 bg-amber-100 rounded-full border border-amber-300">
                        <HiEye className="w-3 h-3 text-amber-600" />
                      </div>
                    </Tooltip>
                  )}
                </div>
              </div>
              <div
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                  stock.isPositive
                    ? "bg-green-100 text-green-700 border border-green-200"
                    : "bg-red-100 text-red-700 border border-red-200"
                }`}
              >
                {stock.isPositive ? (
                  <HiArrowUp size={10} />
                ) : (
                  <HiArrowDown size={10} />
                )}
                {stock.isPositive ? "+" : "-"}
                {formatPercentage(stock.percentage)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StockList;
